import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SummaryApi from './common/index';
import './BlogDetails.css';
import { FiArrowLeft, FiClock, FiShare2, FiBookmark, FiPlayCircle, FiCheck, FiLink2 } from 'react-icons/fi';

const BlogDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const blog = state?.blogData;
  
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isShared, setIsShared] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showToast, setShowToast] = useState("");
  const hasTracked = useRef(false); 


  // Inside BlogDetails.js tracking useEffect
useEffect(() => {
    // 3. Only run if blog_id exists AND we haven't tracked it in this component mount
    if (blog?.blog_id && !hasTracked.current) {
        
        // 4. Create a unique key for this blog so it doesn't re-track on refresh
        const sessionKey = `viewed_blog_${blog.blog_id}`;
        if (sessionStorage.getItem(sessionKey)) {
            return; // Already tracked in this browser session, stop here.
        }

        const recordVisit = async () => {
            hasTracked.current = true; // Mark as tracked immediately
            
            try {
                // Get IP
                let ip = "0.0.0.0";
                try {
                    const ipRes = await fetch("https://api.ipify.org?format=json");
                    const ipData = await ipRes.json();
                    ip = ipData.ip;
                } catch (e) {
                    console.log("IP fetch failed, using fallback");
                }

                // Send to your PHP API
                const response = await fetch(SummaryApi.trackBlogVisit.url, {
                    method: SummaryApi.trackBlogVisit.method,
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        blog_id: blog.blog_id,
                        ip_address: ip
                    }),
                });

                const result = await response.json();
                
                if(result.status) {
                    // 5. Store in sessionStorage so it doesn't count again if they refresh
                    sessionStorage.setItem(sessionKey, 'true');
                    console.log("Visit tracked successfully");
                }
            } catch (error) {
                console.error("Tracking Error:", error);
                hasTracked.current = false; // Reset if it failed so it can try again
            }
        };

        recordVisit();
    }
}, [blog?.blog_id]);


  useEffect(() => {
    window.scrollTo(0, 0);
    if (blog) {
      const saved = JSON.parse(localStorage.getItem('blog_bookmarks') || '[]');
      setIsBookmarked(saved.includes(blog.blog_id));
    }
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [blog]);

  const getSafeHostname = (urlStr) => {
    if (!urlStr) return "";
    try {
      const formattedUrl = urlStr.startsWith('http') ? urlStr : `https://${urlStr}`;
      return new URL(formattedUrl).hostname;
    } catch (e) {
      return urlStr;
    }
  };

  if (!blog) return <div className="error-screen"><h1>404 Not Found</h1></div>;

  const handleShare = async () => {
    const shareUrl = window.location.href;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        triggerSuccess();
        return;
      } catch (err) {}
    }
    try {
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) triggerSuccess();
    } catch (err) {
      alert("Manual copy required: " + shareUrl);
    }
  };

  const triggerSuccess = () => {
    setShowToast("Link Copied to Clipboard!");
    setIsShared(true);
    setTimeout(() => { setShowToast(""); setIsShared(false); }, 3000);
  };

  const handleBookmark = () => {
    let saved = JSON.parse(localStorage.getItem('blog_bookmarks') || '[]');
    if (isBookmarked) {
      saved = saved.filter(id => id !== blog.blog_id);
      setShowToast("Removed from Bookmarks");
    } else {
      saved.push(blog.blog_id);
      setShowToast("Saved to Bookmarks");
    }
    localStorage.setItem('blog_bookmarks', JSON.stringify(saved));
    setIsBookmarked(!isBookmarked);
    setTimeout(() => setShowToast(""), 3000);
  };

  return (
    <div className="editorial-wrapper">
      {showToast && <div className="modern-toast">{showToast}</div>}
      <div className="progress-bar" style={{ width: `${scrollProgress}%` }}></div>

      <aside className="sticky-tools">
        <button onClick={() => navigate(-1)} className="tool-btn" title="Back"><FiArrowLeft /></button>
        <button className={`tool-btn ${isShared ? 'active' : ''}`} onClick={handleShare} title="Share">
          {isShared ? <FiCheck /> : <FiShare2 />}
        </button>
        <button className={`tool-btn ${isBookmarked ? 'active-bookmark' : ''}`} onClick={handleBookmark} title="Bookmark">
          <FiBookmark fill={isBookmarked ? "currentColor" : "none"} />
        </button>
      </aside>

      <main className="editorial-main">
        <header className="editorial-header">
          <div className="meta-row">
            <span className="meta-item"><FiClock /> 5 min read</span>
            <span className="dot" />
            <span className="meta-item">
              {new Date(blog.post_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
          <h1 className="editorial-title">{blog.blog_title}</h1>
          <p className="editorial-subtitle">{blog.blog_subtitle}</p>
        </header>

        <figure className="editorial-hero">
          <img src={blog.cover_image ? `${SummaryApi.BlogImage.url}/${blog.cover_image}` : "https://via.placeholder.com/1200x600"} alt="Cover" />
        </figure>

        <article className="editorial-content">
          <div className="article-rich-text" dangerouslySetInnerHTML={{ __html: blog.blog_content }} />
          {blog.blog_video && (
            <div className="editorial-video-container">
              <video controls className="editorial-video">
                <source src={`${SummaryApi.BlogImage.url}/${blog.blog_video}`} type="video/mp4" />
              </video>
            </div>
          )}
        </article>

        {/* --- UPDATED FOOTER SECTION --- */}
        <footer className="editorial-footer">
          {/* 1. Source URL First */}
          {blog.url && (
            <div className="canonical-source">
              <FiLink2 /> 
              <span>Published at: </span>
              <a href={blog.url} target="_blank" rel="noopener noreferrer">
                {getSafeHostname(blog.url)}
              </a>
            </div>
          )}

          {/* 2. Tags Bottom Last */}
          <div className="footer-tags-container">
            <span className="tags-label">Related Topics:</span>
            <div className="footer-tags-list">
              {blog.tags?.split(',').map((t, i) => t.trim() && (
                <span key={i} className="tag-pill-footer">#{t.trim()}</span>
              ))}
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default BlogDetails;